import React, { ReactNode } from "react"
import {
    Box,
    Container,
    Divider,
    Link,
    List,
    ListItem,
    Sheet,
    Stack,
    Typography,
} from "@mui/joy"
import FullLogo from "./FullLogo";

// ─── Header ────────────────────────────────────────────────────────────────

export const LegalHeader = () => (
    <Sheet
        sx={{
            bgcolor: "var(--ls-navy)",
            px: { xs: 2, md: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: 0,
        }}
    >
        <FullLogo />

        <Link
            href="/"
            sx={{
                color: "var(--ls-pink-light)",
                fontSize: "0.875rem",
                textDecoration: "none",
                opacity: 0.8,
                "&:hover": { opacity: 1 },
            }}
        >
            ← Back to app
        </Link>
    </Sheet>
)

// ─── Hero ──────────────────────────────────────────────────────────────────

type LegalHeroProps = {
    title: string
    updatedAt?: string
}

export const LegalHero = ({ title, updatedAt = "July 2026" }: LegalHeroProps) => (
    <Box
        sx={{
            bgcolor: "var(--ls-navy)",
            px: 2,
            pt: 5,
            pb: 8,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "60px",
                bgcolor: "var(--ls-bg)",
                clipPath: "ellipse(55% 100% at 50% 100%)",
            },
        }}>
        <Box
            sx={{
                display: "inline-block",
                bgcolor: "primary.600",
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.5,
                borderRadius: "2rem",
                mb: 1.5,
            }}>
            Legal
        </Box>

        <Typography
            level="h1"
            sx={{
                fontFamily: "'Playfair Display', serif",
                color: "#fff",
                fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                lineHeight: 1.2,
                mb: 1,
            }}>
            {title}
        </Typography>

        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.9rem" }}>
            Last updated: {updatedAt}
        </Typography>
    </Box>
)

// ─── Table of Contents ─────────────────────────────────────────────────────

type TocItem = { href: string; label: string }

type LegalTocProps = { items: TocItem[] }

export const LegalToc = ({ items }: LegalTocProps) => (
    <Sheet
        variant="outlined"
        sx={{
            borderLeft: "3px solid var(--ls-pink)",
            borderLeftColor:"primary.800",
            borderRadius: "8px",
            p: 2.5,
            mb: 5,
            bgcolor: "#fff",
        }}>
        <Typography
            sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--ls-muted)",
                mb: 1,
            }}
        >
            Contents
        </Typography>

        <List size="sm" sx={{ "--ListItem-paddingY": "0.2rem", "--ListItem-paddingX": "0" }}>
            {items.map((item, i) => (
                <ListItem key={item.href} sx={{ display: "list-item", listStyleType: "decimal", ml: 2 }}>
                    <Link
                        href={item.href}
                        sx={{ color: "primary.800", fontSize: "0.875rem", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                        {item.label}
                    </Link>
                </ListItem>
            ))}
        </List>
    </Sheet>
)

// ─── Section wrapper ───────────────────────────────────────────────────────

type LegalSectionProps = {
    id: string
    num: string
    title: string
    children: ReactNode
}

export const LegalSection = ({ id, num, title, children }: LegalSectionProps) => (
    <Box component="section" id={id} sx={{ mb: 4, scrollMarginTop: "2rem" }}>
        <Box
            sx={{
                display: "inline-block",
                bgcolor: "var(--ls-pink-light)",
                color: "var(--ls-pink)",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                px: 1,
                py: 0.3,
                borderRadius: "4px",
                mb: 0.75,
            }}
        >
            {num}
        </Box>

        <Typography
            level="h3"
            sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1.3rem",
                color: "var(--ls-navy)",
                mb: 1.5,
            }}
        >
            {title}
        </Typography>

        {children}
    </Box>
)

// ─── Highlight box ─────────────────────────────────────────────────────────

type HighlightBoxProps = { children: ReactNode; variant?: "pink" | "warning" }

export const HighlightBox = ({ children, variant = "pink" }: HighlightBoxProps) => (
    <Box
        sx={{
            bgcolor: variant === "pink" ? "var(--ls-pink-light)" : "#fff8e1",
            borderLeft: `3px solid ${variant === "pink" ? "var(--ls-pink)" : "#f59e0b"}`,
            borderRadius: "8px",
            px: 2.5,
            py: 2,
            my: 2,
            "& p, & .MuiTypography-root": {
                color: variant === "pink" ? "var(--ls-navy)" : "#78350f",
                fontSize: "0.9rem",
                margin: 0,
            },
        }}
    >
        {children}
    </Box>
)

// ─── Body text ─────────────────────────────────────────────────────────────

export const LegalText = ({ children }: { children: ReactNode }) => (
    <Typography sx={{ color: "#3d4f63", fontSize: "0.95rem", mb: 1.25, lineHeight: 1.7 }}>
        {children}
    </Typography>
)

// ─── Body list ─────────────────────────────────────────────────────────────

export const LegalList = ({ items }: { items: ReactNode[] }) => (
    <List
        size="sm"
        sx={{
            pl: 2,
            mb: 1.25,
            "--ListItem-paddingY": "0.2rem",
            "--ListItem-paddingX": "0",
        }}
    >
        {items.map((item, i) => (
            <ListItem key={i} sx={{ display: "list-item", listStyleType: "disc", color: "#3d4f63", fontSize: "0.95rem" }}>
                {item}
            </ListItem>
        ))}
    </List>
)

// ─── Footer ────────────────────────────────────────────────────────────────

export const LegalFooter = () => (
    <Box sx={{ textAlign: "center", py: 3, borderTop: "1px solid var(--ls-border)" }}>
        <Typography sx={{ fontSize: "0.8rem", color: "var(--ls-muted)" }}>
            © 2026 ZeeJay Labs ·{" "}
            <Link href="/privacy" sx={{ color: "var(--ls-pink)", textDecoration: "none" }}>Privacy Policy</Link>
            {" · "}
            <Link href="/terms" sx={{ color: "var(--ls-pink)", textDecoration: "none" }}>Terms of Use</Link>
            {" · "}
            <Link href="/data-retention" sx={{ color: "var(--ls-pink)", textDecoration: "none" }}>Data Retention</Link>
        </Typography>
    </Box>
)

// ─── Page shell ────────────────────────────────────────────────────────────

export const LegalPage = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            "--ls-navy": "#0a1628",
            "--ls-pink": "#e91e7a",
            "--ls-pink-light": "#fde8f3",
            "--ls-text": "#1a2332",
            "--ls-muted": "#6b7a8f",
            "--ls-border": "#e2e8f0",
            "--ls-bg": "#f8fafc",
            bgcolor: "var(--ls-bg)",
            minHeight: "100vh",
            fontFamily: "'Inter', sans-serif",
        }}
    >
        {children}
    </Box>
)

export const LegalMain = ({ children }: { children: ReactNode }) => (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
        {children}
    </Container>
)
